/***
*
*   WORKSPACES LIST
*   View and manage all organizations/workspaces
*
**********/

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Input, Select, Space, Typography, Row, Col,
  Button, Tag, Tooltip, Modal, message
} from 'antd';
import {
  ShopOutlined, SearchOutlined, DownloadOutlined,
  EyeOutlined, PoweroffOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ReloadOutlined, TeamOutlined,
  UserOutlined
} from '@ant-design/icons';
import { fetchWorkspaces, deactivateWorkspace, activateWorkspace, exportAllWorkspaces, invalidateWorkspacesCache } from '../store/slices/workspacesSlice';
import { fetchPlans } from '../store/slices/plansSlice';
import { getCacheKey } from '../store/cacheUtils';
import { Animate, usePermissions } from 'components/lib';

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

export function WorkspacesList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [filters, setFilters] = useState({
    page: 1,
    limit: 25,
    search: '',
    plan: '',
    status: '',
    sort: 'createdAt',
    order: 'desc'
  });

  const [searchInput, setSearchInput] = useState('');

  const workspacesState = useSelector((state) => state.workspaces);
  const plansState = useSelector((state) => state.plans);
  const cacheKey = getCacheKey(filters);
  const query = window.location.search;

  useEffect(() => {
    const urlParams = new URLSearchParams(query);
    const plan = urlParams.get('plan') || '';
    const status = urlParams.get('status') || '';
    setFilters(prev => ({ ...prev, plan, status }));
    setSearchInput('');
  }, [query]);

  useEffect(() => {
    dispatch(fetchPlans());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchWorkspaces(filters));
  }, [dispatch, filters]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const data = workspacesState.data[cacheKey];
  const loading = workspacesState.loading;

  const handleToggleStatus = (workspace) => {
    const action = workspace.active ? 'deactivate' : 'activate';
    confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Workspace`,
      content: workspace.active
        ? `Are you sure you want to deactivate "${workspace.name}"? Users will not be able to log in.`
        : `Are you sure you want to activate "${workspace.name}"?`,
      okText: action.charAt(0).toUpperCase() + action.slice(1),
      okButtonProps: { danger: workspace.active },
      onOk: async () => {
        try {
          if (workspace.active) {
            await dispatch(deactivateWorkspace(workspace.id || workspace._id)).unwrap();
          } else {
            await dispatch(activateWorkspace(workspace.id || workspace._id)).unwrap();
          }
          message.success(`Workspace ${action}d successfully`);
          dispatch(invalidateWorkspacesCache());
          dispatch(fetchWorkspaces(filters));
        } catch (err) {
          message.error(`Failed to ${action} workspace`);
        }
      },
    });
  };

  const handleExportAll = async () => {
    try {
      await dispatch(exportAllWorkspaces({ plan: filters.plan, status: filters.status })).unwrap();
      message.success('Export started');
    } catch {
      message.error('Failed to export data');
    }
  };

  const handleRefresh = () => {
    dispatch(invalidateWorkspacesCache());
    dispatch(fetchWorkspaces(filters));
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button type="link" onClick={() => navigate(`/workspaces/${record.id || record._id}`)}>
          {text}
        </Button>
      ),
      sorter: true,
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Plan',
      dataIndex: 'plan_name',
      key: 'plan_name',
      render: (text, record) => <Tag color="blue">{text || record.plan || 'N/A'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'} icon={active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Owner Email',
      dataIndex: 'owner_email',
      key: 'owner_email',
      ellipsis: true,
    },
    {
      title: 'Users',
      dataIndex: 'user_count',
      key: 'user_count',
      render: (count) => <Space><TeamOutlined /> {count || 0}</Space>,
    },
    {
      title: 'Employees',
      dataIndex: 'employee_count',
      key: 'employee_count',
      render: (count) => <Space><UserOutlined /> {count || 0}</Space>,
    },
    {
      title: 'Created',
      dataIndex: 'date_created',
      key: 'date_created',
      render: (date, record) => new Date(date || record.createdAt).toLocaleDateString(),
    },
    {
      ...(can('workspaces', 'view') || can('workspaces', 'edit')) && {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space>
            {can('workspaces', 'view') && (
              <Tooltip title="View Details">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => navigate(`/workspaces/${record.id || record._id}`)}
                />
              </Tooltip>
            )}
            {can('workspaces', 'edit') && (
              <Tooltip title={record.active ? 'Deactivate' : 'Activate'}>
                <Button
                  type="text"
                  danger={record.active}
                  icon={<PoweroffOutlined />}
                  onClick={() => handleToggleStatus(record)}
                />
              </Tooltip>
            )}
          </Space>
        ),
      }
    },
  ];

  const tableData = data?.results?.map(ws => ({
    ...ws,
    key: ws.id || ws._id,
  })) || [];

  return (
    <Animate type="pop">
      <div>
        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <ShopOutlined style={{ marginRight: 8 }} />
              Workspaces
            </Title>
            <Text type="secondary">Manage all workspaces and organizations</Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
            {can('workspaces', 'view') && (
              <Button icon={<DownloadOutlined />} type="primary" onClick={handleExportAll}>Export All</Button>
            )}
          </Space>
        </div>

        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Input
                placeholder="Search workspaces..."
                prefix={<SearchOutlined />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={8}>
              <Select
                placeholder="All Plans"
                allowClear
                style={{ width: '100%' }}
                value={filters.plan || undefined}
                onChange={(val) => setFilters(prev => ({ ...prev, plan: val || '', page: 1 }))}
              >
                {plansState.data?.list?.map(plan => (
                  <Option key={plan.value} value={plan.value}>{plan.label}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8}>
              <Select
                placeholder="All Status"
                allowClear
                style={{ width: '100%' }}
                value={filters.status || undefined}
                onChange={(val) => setFilters(prev => ({ ...prev, status: val || '', page: 1 }))}
              >
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={{
              current: filters.page,
              pageSize: filters.limit,
              total: data?.total || 0,
              onChange: (page) => setFilters(prev => ({ ...prev, page })),
              showTotal: (total) => `Total ${total} workspaces`,
            }}
          />
        </Card>
      </div>
    </Animate>
  );
}